import { useEffect, useState, type FormEvent } from "react";
import { Save } from "lucide-react";
import { Button, Notice } from "../components/ui";
import { apiRequest, jsonBody } from "../lib/api";
import type { ApiRecord } from "../lib/types";

interface FormState {
  shippingV2Enabled: boolean;
  shippingV2ActivationAllowed: boolean;
  originLatitude: number | null;
  originLongitude: number | null;
  outsideRatePerKm: number | null;
  distanceRounding: "CEIL" | "FLOOR";
  freeRadiusEnabled: boolean;
  freeRadiusKm: number | null;
  freeRadiusScope: "OUTSIDE_DENPASAR_ONLY" | "ALL_AREAS";
  maximumDeliveryDistance: number | null;
}

const empty: FormState = {
  shippingV2Enabled: false,
  shippingV2ActivationAllowed: false,
  originLatitude: null,
  originLongitude: null,
  outsideRatePerKm: null,
  distanceRounding: "CEIL",
  freeRadiusEnabled: false,
  freeRadiusKm: null,
  freeRadiusScope: "OUTSIDE_DENPASAR_ONLY",
  maximumDeliveryDistance: null,
};

function numberOrNull(value: unknown): number | null {
  if (value === null || value === undefined || value === "") return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}

function normalize(row: ApiRecord): FormState {
  return {
    shippingV2Enabled: row.shipping_v2_enabled === 1 || row.shippingV2Enabled === true,
    shippingV2ActivationAllowed: row.shipping_v2_activation_allowed === true || row.shippingV2ActivationAllowed === true,
    originLatitude: numberOrNull(row.origin_latitude ?? row.originLatitude),
    originLongitude: numberOrNull(row.origin_longitude ?? row.originLongitude),
    outsideRatePerKm: numberOrNull(row.outside_rate_per_km ?? row.outsideRatePerKm),
    distanceRounding: (row.distance_rounding ?? row.distanceRounding ?? "CEIL") as FormState["distanceRounding"],
    freeRadiusEnabled: row.free_radius_enabled === 1 || row.freeRadiusEnabled === true,
    freeRadiusKm: numberOrNull(row.free_radius_km ?? row.freeRadiusKm),
    freeRadiusScope: (row.free_radius_scope ?? row.freeRadiusScope ?? "OUTSIDE_DENPASAR_ONLY") as FormState["freeRadiusScope"],
    maximumDeliveryDistance: numberOrNull(row.maximum_delivery_distance ?? row.maximumDeliveryDistance),
  };
}

export function AdminSettingsPage() {
  const [form, setForm] = useState<FormState>(empty);
  const [busy, setBusy] = useState(false);
  const [notice, setNotice] = useState<string>();

  useEffect(() => {
    void apiRequest<ApiRecord>("/api/admin/branch/shipping-v2")
      .then((row) => setForm(normalize(row)))
      .catch((error: unknown) => setNotice(error instanceof Error ? error.message : "Pengaturan belum dapat dimuat."));
  }, []);

  const save = async (event: FormEvent) => {
    event.preventDefault();
    setBusy(true);
    setNotice(undefined);
    try {
      const payload = {
        shippingV2Enabled: form.shippingV2Enabled,
        originLatitude: form.originLatitude,
        originLongitude: form.originLongitude,
        outsideRatePerKm: form.outsideRatePerKm,
        distanceRounding: form.distanceRounding,
        freeRadiusEnabled: form.freeRadiusEnabled,
        freeRadiusKm: form.freeRadiusKm,
        freeRadiusScope: form.freeRadiusScope,
        maximumDeliveryDistance: form.maximumDeliveryDistance,
      };
      await apiRequest("/api/admin/branch/shipping-v2", { method: "PATCH", ...jsonBody(payload) });
      setNotice(form.shippingV2Enabled
        ? "Shipping V2 aktif: radius gratis berlaku dari cabang, lalu hanya kelebihan jarak yang dikenakan tarif per km."
        : "Shipping V2 nonaktif. Tampilan dan ongkir lama kembali digunakan untuk cabang ini.");
    } catch (error) {
      setNotice(error instanceof Error ? error.message : "Pengaturan belum berhasil disimpan.");
    } finally {
      setBusy(false);
    }
  };

  const numberField = (key: keyof Pick<FormState, "originLatitude" | "originLongitude" | "outsideRatePerKm" | "freeRadiusKm" | "maximumDeliveryDistance">, label: string) => (
    <label>{label}<input type="number" step="any" value={form[key] ?? ""} onChange={(event) => setForm({ ...form, [key]: event.target.value === "" ? null : Number(event.target.value) })} /></label>
  );

  return (
    <div className="dashboard-page">
      <div className="dashboard-heading"><div><span className="eyebrow">Akses cabang</span><h1>Pengaturan Admin</h1><p>Pilih ongkir lama atau Shipping V2 berbasis jarak untuk cabang ini.</p></div></div>
      {notice && <Notice>{notice}</Notice>}
      <form className="dashboard-panel modal-form" onSubmit={(event) => void save(event)}>
        <h2>Persiapan Shipping V2</h2>
        <label>Mode perhitungan ongkir<select value={form.shippingV2Enabled ? "COMBINED" : "LEGACY"} disabled={!form.shippingV2Enabled && !form.shippingV2ActivationAllowed} onChange={(event) => setForm({ ...form, shippingV2Enabled: event.target.value === "COMBINED" })}><option value="LEGACY">Ongkir lama</option><option value="COMBINED">Shipping V2: radius gratis + tarif kelebihan per km</option></select><span>{form.shippingV2Enabled ? "Jarak dihitung dari cabang ke patokan pelanggan. Ongkir dasar tetap mengikuti aturan toko; setelah syarat gratis terpenuhi, hanya jarak di atas radius yang dikenakan tarif." : "Semua tujuan memakai ongkir lama cabang."}</span></label>
        <div className="two-field-grid">{numberField("originLatitude", "Latitude asal cabang")}{numberField("originLongitude", "Longitude asal cabang")}</div>
        <div className="two-field-grid">{numberField("outsideRatePerKm", "Tarif kelebihan radius per km (Rp)")}{numberField("maximumDeliveryDistance", "Jarak maksimum (km)")}</div>
        <label>Pembulatan jarak<select value={form.distanceRounding} onChange={(event) => setForm({ ...form, distanceRounding: event.target.value as FormState["distanceRounding"] })}><option value="CEIL">Ke atas (CEIL)</option><option value="FLOOR">Ke bawah (FLOOR)</option></select></label>
        <label className="toggle-setting"><input type="checkbox" checked={form.freeRadiusEnabled} onChange={(event) => setForm({ ...form, freeRadiusEnabled: event.target.checked })} /><span><strong>Aktifkan radius gratis</strong><small>Berlaku dari titik asal cabang ke semua alamat pengiriman.</small></span></label>
        {form.freeRadiusEnabled && <div className="two-field-grid">{numberField("freeRadiusKm", "Radius gratis (km)")}<label>Cakupan radius<input value="Semua wilayah dari cabang" readOnly /><span>Gratis jika syarat minimum belanja terpenuhi. Jika belum, ongkir dasar tetap berlaku; jarak di atas radius ditambahkan sesuai tarif per km.</span></label></div>}
        <Button type="submit" busy={busy} icon={<Save />}>Simpan Persiapan</Button>
      </form>
    </div>
  );
}
